import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Trash2 } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import { useGoalTemplatesApi, departmentAsync } from './config';
import { positionAsync } from '../positions/config';
import BulkAssignModal from './components/BulkAssignModal';
import MilestoneStepsEditor, {
  fromMilestoneSteps, isMilestoneStepsValid, toMilestoneStepsPayload,
} from '../../components/ui/MilestoneStepsEditor';
import { GOAL_TYPES, THRESHOLD_OPERATORS } from '../../components/performance/goalTypes';

// Skorlama Faz F, Task 4 (spec §4, task-3-report.md §1.4) — Hedef Şablonları
// yönetim sekmesi. RatingScalesTab.js'in (Skorlama Plan 2, Task 10) BİREBİR
// aynı deseni: GenericFormDrawer yalnız KABUK olarak kullanılır (başlık/
// kapat/Kaydet-İptal), tüm alanlar `schema=[]` + `children` içinde kendi
// taslak state'imizle yönetilir — çünkü goalType'a göre KOŞULLU bölüm
// (OKR→KR başlık listesi editörü, KPI→gösterge alanları) GenericFormDrawer'ın
// statik şema dizisiyle doğal olarak ifade edilemez.
//
// Alan adları BE sözleşmesiyle (task-3-report.md §1.4) birebir: name/
// departmentId/positionId/goalType/title/description/krTitles/
// kpiIndicatorName/kpiUnit/kpiTargetValue/kpiBaselineValue/kpiCapPct — hepsi
// TÜMÜYLE opsiyonel (PeriodGoal'dan FARKLI, şablon kısmi doldurulabilir).
//
// KPI alan etiketleri (title/description/goalType dahil) BİLİNÇLİ olarak
// `performance.goal.*` namespace'inden REUSE edilir (name HARİÇ) — bunlar
// PeriodGoal ile GoalTemplate arasında LİTERAL AYNI alanlar (task-3-report.md
// §1.4: "TÜMÜ opsiyonel" notu bile bu 1:1 eşlemeyi doğruluyor), FeedbackForm/
// RatingScale gibi bağımsız bir feature-vertical DEĞİL — PerformanceSettingsPage
// .js'in weight alanlarını (`performance.settings.weights.*`) hem genel
// ayarlarda HEM FeedbackForm override'ında paylaşmasıyla AYNI gerekçe.

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || '1fr 1fr'};
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 10px;
  border-bottom: 1px solid #E8ECF0;
  padding-bottom: 6px;
`;

const Hint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 4px 0 12px;
`;

const KrRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
  background: #F5F7FA;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
`;

const KrControls = styled.div`
  display: flex;
  gap: 4px;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  color: #667;
  &:hover:not(:disabled) { border-color: #0291EF; color: #0291EF; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const RemoveIconBtn = styled(IconBtn)`
  &:hover:not(:disabled) { border-color: #E53935; color: #E53935; }
`;

const SaveErrorText = styled.p`
  font-size: 12px;
  color: #B02A37;
  margin: 8px 0;
  font-weight: 600;
`;

const KPI_UNITS = ['numeric', 'currency', 'percent'];

const emptyKrTitle = () => ({ _key: Math.random().toString(36).slice(2), title: '' });

export default function GoalTemplatesTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('PM');

  const {
    data, fetching, createTemplate, updateTemplate, deleteTemplate,
  } = useGoalTemplatesApi();

  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [nameDraft, setNameDraft] = useState('');
  const [departmentId, setDepartmentId] = useState(null);
  const [positionId, setPositionId] = useState(null);
  const [goalType, setGoalType] = useState('okr');
  const [titleDraft, setTitleDraft] = useState('');
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [krTitles, setKrTitles] = useState([]);
  const [kpiIndicatorName, setKpiIndicatorName] = useState('');
  const [kpiUnit, setKpiUnit] = useState('numeric');
  const [kpiTargetValue, setKpiTargetValue] = useState('');
  const [kpiBaselineValue, setKpiBaselineValue] = useState('');
  const [kpiCapPct, setKpiCapPct] = useState('');
  const [kpiDirection, setKpiDirection] = useState('increase');
  // P3 (2026-10-08) — kilometre taşı adımları (opsiyonel; verildiyse toplam 100) ve eşik koşulu.
  const [milestoneSteps, setMilestoneSteps] = useState([]);
  const [thresholdOperator, setThresholdOperator] = useState('gte');
  const [thresholdValue, setThresholdValue] = useState('');
  const [saveError, setSaveError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [busy, setBusy] = useState(false);
  // Faz J (spec 2026-08-08-pm-hijyen-faz-j-design.md §4, J5, Task 4) —
  // "Toplu Ata" modalı, satırın kendisini (id + name — modal başlığı için) taşır.
  const [bulkAssignRow, setBulkAssignRow] = useState(null);

  // RatingScalesTab.js'in nameDraft/levels hydrate deseninin aynısı — drawer
  // her açılışta (yeni VEYA satır düzenleme) taslağı sıfırdan kurar.
  useEffect(() => {
    if (!drawer.open) return;
    const row = drawer.row;
    setNameDraft(row?.name || '');
    setDepartmentId(row?.departmentId || null);
    setPositionId(row?.positionId || null);
    setGoalType(row?.goalType || 'okr');
    setTitleDraft(row?.title || '');
    setDescriptionDraft(row?.description || '');
    setKrTitles((row?.krTitles && row.krTitles.length > 0)
      ? row.krTitles.map((title) => ({ _key: Math.random().toString(36).slice(2), title }))
      : []);
    setKpiIndicatorName(row?.kpiIndicatorName || '');
    setKpiUnit(row?.kpiUnit || 'numeric');
    setKpiTargetValue(row?.kpiTargetValue != null ? String(row.kpiTargetValue) : '');
    setKpiBaselineValue(row?.kpiBaselineValue != null ? String(row.kpiBaselineValue) : '');
    setKpiCapPct(row?.kpiCapPct != null ? String(row.kpiCapPct) : '');
    // Yönsüz eski şablonda GoalEditor.applyTemplate ile AYNI çıkarım: baz > hedef → azalış.
    setKpiDirection(row?.kpiDirection
      || (row?.kpiBaselineValue != null && row?.kpiTargetValue != null
          && Number(row.kpiBaselineValue) > Number(row.kpiTargetValue)
        ? 'decrease' : 'increase'));
    setMilestoneSteps(fromMilestoneSteps(row?.milestoneSteps));
    setThresholdOperator(row?.thresholdOperator || 'gte');
    setThresholdValue(row?.thresholdValue != null ? String(row.thresholdValue) : '');
    setSaveError('');
  }, [drawer.open, drawer.row]);

  const openNew = useCallback(() => setDrawer({ open: true, row: null }), []);
  const openEdit = useCallback((row) => setDrawer({ open: true, row }), []);
  const closeDrawer = useCallback(() => setDrawer({ open: false, row: null }), []);

  const addKrTitle = useCallback(() => setKrTitles((prev) => [...prev, emptyKrTitle()]), []);
  const removeKrTitle = useCallback((idx) => {
    setKrTitles((prev) => prev.filter((_, i) => i !== idx));
  }, []);
  const updateKrTitleAt = useCallback((idx, value) => {
    setKrTitles((prev) => prev.map((k, i) => (i === idx ? { ...k, title: value } : k)));
  }, []);
  const moveKrTitle = useCallback((idx, dir) => {
    setKrTitles((prev) => {
      const otherIdx = idx + dir;
      if (otherIdx < 0 || otherIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[otherIdx]] = [next[otherIdx], next[idx]];
      return next;
    });
  }, []);

  const goalTypeOptions = useMemo(
    () => GOAL_TYPES.map((gt) => ({ id: gt, name: t(`performance.goal.goalType.${gt}`) })),
    [t],
  );
  const thresholdOperatorOptions = useMemo(
    () => THRESHOLD_OPERATORS.map((op) => ({ id: op, name: t(`performance.goal.threshold.operator.${op}`) })),
    [t],
  );
  const kpiUnitOptions = useMemo(
    () => KPI_UNITS.map((u) => ({ id: u, name: t(`performance.goal.kpiUnit.${u}`) })),
    [t],
  );
  const kpiDirectionOptions = useMemo(
    () => ['increase', 'decrease'].map((d) => ({ id: d, name: t(`performance.goal.kpiDirection.${d}`) })),
    [t],
  );

  // Boş/whitespace KR başlıkları BE tarafında zaten filtrelenir+trim'lenir
  // (task-3-report.md §1.4) — istemci burada da AYNI temizliği yapar (defensive,
  // ObTaskTemplatesPage.toPayload'ın "empty title silently dropped" deseni).
  const handleSave = useCallback(async () => {
    const trimmedName = nameDraft.trim();
    if (!trimmedName) {
      setSaveError(t('goalTemplates.nameRequiredHint'));
      return false;
    }
    const trimmedTitle = titleDraft.trim();
    if (!trimmedTitle) {
      setSaveError(t('goalTemplates.titleRequiredHint'));
      return false;
    }
    // P3 — kilometre taşı şablonunda adım verildiyse toplam TAM 100 (backend kapısının ikizi).
    if (goalType === 'milestone' && milestoneSteps.length > 0 && !isMilestoneStepsValid(milestoneSteps)) {
      setSaveError(t('performance.goal.milestone.weightTotalHint'));
      return false;
    }
    setSaveError('');

    const sharesIndicator = goalType === 'kpi' || goalType === 'threshold';
    const payload = {
      name: trimmedName,
      departmentId: departmentId || null,
      positionId: positionId || null,
      goalType,
      title: trimmedTitle,
      description: descriptionDraft.trim() || undefined,
      krTitles: goalType === 'okr'
        ? krTitles.map((k) => k.title.trim()).filter(Boolean)
        : undefined,
      kpiIndicatorName: sharesIndicator ? (kpiIndicatorName.trim() || undefined) : undefined,
      kpiUnit: goalType === 'kpi' || (goalType === 'threshold' && thresholdOperator !== 'binary') ? kpiUnit : undefined,
      kpiTargetValue: goalType === 'kpi' && kpiTargetValue !== '' ? Number(kpiTargetValue) : undefined,
      kpiBaselineValue: goalType === 'kpi' && kpiBaselineValue !== '' ? Number(kpiBaselineValue) : undefined,
      kpiCapPct: goalType === 'kpi' && kpiCapPct !== '' ? Number(kpiCapPct) : undefined,
      kpiDirection: goalType === 'kpi' ? kpiDirection : undefined,
      milestoneSteps: goalType === 'milestone'
        ? toMilestoneStepsPayload(milestoneSteps, { withIds: false, withDueDate: false })
        : undefined,
      thresholdOperator: goalType === 'threshold' ? thresholdOperator : undefined,
      thresholdValue: goalType === 'threshold' && thresholdOperator !== 'binary' && thresholdValue !== ''
        ? Number(thresholdValue) : undefined,
    };

    return drawer.row
      ? await updateTemplate(drawer.row.id, payload)
      : await createTemplate(payload);
  }, [
    nameDraft, titleDraft, departmentId, positionId, goalType, descriptionDraft, krTitles,
    kpiIndicatorName, kpiUnit, kpiTargetValue, kpiBaselineValue, kpiCapPct, kpiDirection, drawer.row,
    milestoneSteps, thresholdOperator, thresholdValue,
    updateTemplate, createTemplate, t,
  ]);

  const confirmDelete = useCallback(async () => {
    setBusy(true);
    await deleteTemplate(deleteConfirm.id);
    setBusy(false);
    setDeleteConfirm({ isOpen: false, id: null, name: '' });
  }, [deleteTemplate, deleteConfirm.id]);

  const actionOptions = useMemo(() => {
    if (!canManage) return [];
    return [
      { type: 'edit', label: t('common.edit'), onClick: openEdit },
      // Faz J (Task 4, spec §4) — şablondan toplu hedef atama; "Düzenle"nin
      // hemen ardına eklendi (aynı satırın en sık kullanılan iki eylemi).
      // type:'publish' → OptionsMenu.iconMap'te Send ikonu (submit_for_approval
      // ile AYNI ikon ailesi — 'send' KAYITLI bir tür DEĞİL, Eye fallback'ine
      // düşerdi, semantik olarak yanlış olurdu).
      { type: 'publish', label: t('bulkAssign.actionLabel'), onClick: (row) => setBulkAssignRow(row) },
      { type: 'divider' },
      {
        type: 'delete',
        label: t('common.delete'),
        onClick: (row) => setDeleteConfirm({ isOpen: true, id: row.id, name: row.name }),
      },
    ];
  }, [canManage, t, openEdit]);

  // Kapsam sütunu — ad ÇÖZÜLMEZ (departmentId/positionId "soft reference —
  // FK yok", task-3-report.md §1.4), yalnız hangi boyutun dolu olduğu
  // gösterilir. ObTaskTemplatesPage.js'in kendi liste kolonları da AYNI şekilde
  // departmentId/positionId'yi HİÇ göstermiyor (yalnız drawer'da) — burada
  // ekstra API çağrısı gerektirmeyen bu özet gösterim bilinçli bir orta yol.
  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('goalTemplates.nameLabel'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'goalType',
      label: t('performance.goal.fields.goalType'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => t(`performance.goal.goalType.${v}`, { defaultValue: v }),
      },
    },
    {
      name: 'scope',
      label: t('goalTemplates.scopeColumn'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return '-';
          if (row.positionId) return t('goalTemplates.scopePosition');
          if (row.departmentId) return t('goalTemplates.scopeDepartment');
          return t('goalTemplates.scopeGeneral');
        },
      },
    },
  ], [t, data]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const drawerTitle = useMemo(
    () => (drawer.row ? t('goalTemplates.edit') : t('goalTemplates.new')),
    [drawer.row, t],
  );

  return (
    <div>
      <GenericFormDrawer
        open={drawer.open}
        onClose={closeDrawer}
        title={drawerTitle}
        schema={[]}
        onSave={handleSave}
      >
        <FieldGroup>
          <TextInput
            label={t('goalTemplates.nameLabel')}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            required
          />
        </FieldGroup>

        <SectionTitle>{t('goalTemplates.scopeSectionTitle')}</SectionTitle>
        <Hint>{t('goalTemplates.scopeHint')}</Hint>
        <Row>
          <AsyncGenericSelect
            label={t('goalTemplates.departmentLabel')}
            value={departmentId}
            onChange={setDepartmentId}
            onSearch={departmentAsync.onSearch}
            onGetById={departmentAsync.onGetById}
          />
          <AsyncGenericSelect
            label={t('goalTemplates.positionLabel')}
            value={positionId}
            onChange={setPositionId}
            onSearch={positionAsync.onSearch}
            onGetById={positionAsync.onGetById}
            renderOption={positionAsync.renderOption}
            searchPlaceholder={t('position.pickerSearchPlaceholder')}
          />
        </Row>

        <FieldGroup>
          <GenericSelect
            label={t('performance.goal.fields.goalType')}
            value={goalType}
            onChange={setGoalType}
            options={goalTypeOptions}
            idKey="id"
            labelKey="name"
          />
        </FieldGroup>
        <FieldGroup>
          <TextInput
            label={t('performance.goal.fields.title')}
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            required
          />
        </FieldGroup>
        <FieldGroup>
          <TextInput
            label={t('performance.goal.fields.description')}
            type="textarea"
            rows={3}
            value={descriptionDraft}
            onChange={(e) => setDescriptionDraft(e.target.value)}
          />
        </FieldGroup>

        {/* P3 — kilometre taşı şablonu: adım başlıkları + ağırlıklar (son tarih hedefe kopyalanınca girilir). */}
        {goalType === 'milestone' && (
          <>
            <SectionTitle>{t('performance.goal.milestone.steps')}</SectionTitle>
            <MilestoneStepsEditor value={milestoneSteps} onChange={setMilestoneSteps} showDueDate={false} />
          </>
        )}

        {/* P3 — eşik/ikili şablonu: gösterge + operatör + eşik değeri + birim (tümü opsiyonel). */}
        {goalType === 'threshold' && (
          <>
            <SectionTitle>{t('performance.goal.threshold.condition')}</SectionTitle>
            <FieldGroup>
              <TextInput
                label={t('performance.goal.threshold.indicator')}
                value={kpiIndicatorName}
                onChange={(e) => setKpiIndicatorName(e.target.value)}
              />
            </FieldGroup>
            <Row $cols={thresholdOperator === 'binary' ? '1fr' : '1fr 1fr 1fr'}>
              <FieldGroup>
                <GenericSelect
                  label={t('performance.goal.threshold.operatorLabel')}
                  value={thresholdOperator}
                  onChange={setThresholdOperator}
                  options={thresholdOperatorOptions}
                  idKey="id"
                  labelKey="name"
                />
              </FieldGroup>
              {thresholdOperator !== 'binary' && (
                <FieldGroup>
                  <TextInput
                    label={t('performance.goal.threshold.value')}
                    type="number"
                    value={thresholdValue}
                    onChange={(e) => setThresholdValue(e.target.value)}
                  />
                </FieldGroup>
              )}
              {thresholdOperator !== 'binary' && (
                <FieldGroup>
                  <GenericSelect
                    label={t('performance.goal.kpiUnitLabel')}
                    value={kpiUnit}
                    onChange={setKpiUnit}
                    options={kpiUnitOptions}
                    idKey="id"
                    labelKey="name"
                  />
                </FieldGroup>
              )}
            </Row>
          </>
        )}

        {goalType === 'okr' && (
          <>
            <SectionTitle>{t('performance.goal.keyResults')}</SectionTitle>
            <Hint>{t('goalTemplates.krTitlesHint')}</Hint>
            {krTitles.map((kr, idx) => (
              <KrRow key={kr._key}>
                <TextInput
                  label={t('performance.goal.keyResultTitle')}
                  value={kr.title}
                  onChange={(e) => updateKrTitleAt(idx, e.target.value)}
                />
                <KrControls>
                  <IconBtn
                    type="button"
                    title={t('goalTemplates.moveUp')}
                    disabled={idx === 0}
                    onClick={() => moveKrTitle(idx, -1)}
                  >
                    <ArrowUp size={13} />
                  </IconBtn>
                  <IconBtn
                    type="button"
                    title={t('goalTemplates.moveDown')}
                    disabled={idx === krTitles.length - 1}
                    onClick={() => moveKrTitle(idx, 1)}
                  >
                    <ArrowDown size={13} />
                  </IconBtn>
                  <RemoveIconBtn type="button" title={t('common.delete')} onClick={() => removeKrTitle(idx)}>
                    <Trash2 size={13} />
                  </RemoveIconBtn>
                </KrControls>
              </KrRow>
            ))}
            <Button type="button" variant="outline" onClick={addKrTitle}>
              + {t('performance.goal.addKeyResult')}
            </Button>
          </>
        )}
        {goalType === 'kpi' && (
          <>
            <Row>
              <FieldGroup>
                <TextInput
                  label={t('performance.goal.kpiIndicatorName')}
                  value={kpiIndicatorName}
                  onChange={(e) => setKpiIndicatorName(e.target.value)}
                />
              </FieldGroup>
              <FieldGroup>
                <GenericSelect
                  label={t('performance.goal.kpiUnitLabel')}
                  value={kpiUnit}
                  onChange={setKpiUnit}
                  options={kpiUnitOptions}
                  idKey="id"
                  labelKey="name"
                />
              </FieldGroup>
              <FieldGroup>
                <GenericSelect
                  label={t('performance.goal.kpiDirectionLabel')}
                  value={kpiDirection}
                  onChange={setKpiDirection}
                  options={kpiDirectionOptions}
                  idKey="id"
                  labelKey="name"
                />
              </FieldGroup>
            </Row>
            <Row $cols="1fr 1fr 1fr">
              <FieldGroup>
                <TextInput
                  label={t('performance.goal.kpiTargetValue')}
                  type="number"
                  value={kpiTargetValue}
                  onChange={(e) => setKpiTargetValue(e.target.value)}
                />
              </FieldGroup>
              <FieldGroup>
                <TextInput
                  label={t('performance.goal.kpiBaselineValue')}
                  type="number"
                  value={kpiBaselineValue}
                  onChange={(e) => setKpiBaselineValue(e.target.value)}
                />
                <Hint>{t('performance.goal.kpiBaselineValueHint')}</Hint>
              </FieldGroup>
              <FieldGroup>
                <TextInput
                  label={t('performance.goal.kpiCapPct')}
                  type="number"
                  min={100}
                  max={300}
                  value={kpiCapPct}
                  onChange={(e) => setKpiCapPct(e.target.value)}
                />
                <Hint>{t('performance.goal.kpiCapPctHint')}</Hint>
              </FieldGroup>
            </Row>
          </>
        )}

        {saveError && <SaveErrorText>{saveError}</SaveErrorText>}
      </GenericFormDrawer>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={busy}
      />

      {bulkAssignRow && (
        <BulkAssignModal
          template={bulkAssignRow}
          onClose={() => setBulkAssignRow(null)}
        />
      )}

      <DataTable
        title={t('goalTemplates.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        serverSide={false}
        canCreateNewRecord={canManage}
        onNewButtonClick={openNew}
        onRowClick={canManage ? openEdit : undefined}
      />
    </div>
  );
}
